import { useContext, useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";
import useRoom from "../../hooks/useRoom";
import AuthContext from "../../context/AuthContext";
import VideoContext from "../../context/VideoContext";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { hostHeartbeat } from "../../Services/videoService";
import "../../components/VideoStream/videoStyles.css";
import RecordingModal from "components/Modal/RecordingModal";
import AllowMultipleGuestsModal from "components/Modal/AllowMultipleGuestsModal";
import DemoInCallModal from "components/Modal/DemoInCallModal";
import DemoContext from "context/DemoContext";
import SocketContext from "context/SocketContext";
import { useLocation, useNavigate } from "react-router-dom";
import PurebrandLoader from "components/Loader/PurebrandLoader";
import { Modal, Button } from "react-bootstrap";
import { message } from 'antd';
import logger from 'utils/logger';
import { MODERATION_STATUS_POLL_MS, VIDEO_MEETING_PERMISSION_MODAL } from "utils/constants";
import { useNetworkPing } from "hooks/useNetworkQualityMonitor";
import NetworkPopup from "components/VideoStream/NetworkNotification";
import { NETWORK_PING_URL, NETWORK_PING_INTERVAL, NETWORK_PING_TIMEOUT } from "components/VideoStream/constants";
import { QuestionBannerOverlay } from "components/VideoStream/QuestionBanner";

/**
 * VideoMeeting Component
 *
 * Main component for handling video meeting functionality using Zego Cloud SDK.
 * This component manages the video call interface, participant connections, recording,
 * and commission tracking for hosts waiting for guests.
 *
 * @component
 * @function VideoMeeting
 * @returns {JSX.Element} The video meeting interface component.
 *
 * @description
 * This component provides the following features:
 * - Video and audio call management via Zego Cloud SDK
 * - Real-time participant tracking and connection status
 * - Recording functionality for meetings
 * - Commission tracking overlay for hosts
 * - Permission handling for iOS/Mac devices
 * - Test meeting mode support
 * - Guest attendance tracking
 * - Page refresh protection (redirects to waiting room)
 *
 * @features
 * - Handles both host and guest user types
 * - Supports test meetings (TestUser or test- room IDs)
 * - Displays commission information overlay while waiting for guests
 * - Manages media stream cleanup on component unmount
 * - Integrates with WebSocket for room join notifications
 *
 * @dependencies
 * - useRoom hook for Zego room management
 * - AuthContext for user authentication
 * - VideoContext for recording functionality
 * - DemoContext for demo status
 * - SocketContext for WebSocket messaging
 *
 * @state
 * - Video/audio status tracking
 * - Participant list management
 * - Overlay visibility control
 * - Permission modal state
 * - Loading states
 * - Error handling
 */
export default function VideoMeeting() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { AuthenticatedDemoStatus, publicDemoStatus } = useContext(DemoContext);
  const { user, loggedIn } = useContext(AuthContext);
  const { record } = useContext(VideoContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [showMultipleGuestsModal, setShowMultipleGuestsModal] = useState(false);
  const [showComissionAlert, setShowComissionAlert] = useState(false);
  const [hasShownAlert, setHasShownAlert] = useState(false); // Flag to ensure modal shows once
  const [videoStatus, setVideoStatus] = useState(state?.videoState);
  const [audioStatus, setAudioStatus] = useState(state?.audioState);
  //screen share
  const [screenShareStatus, setScreenShareStatus] = useState(false);
  const [demoInfo, setDemoInfo] = useState(null);
  const [hostAttendMeeting, setHostAttendMeeting] = useState(null);
  const [showA, setShowA] = useState(true);
  const toggleShowA = () => setShowA(!showA);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [showReScheduleModal, setShowRescheduleModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loader state

  // State to handle overlay visibility
  const [showOverlay, setShowOverlay] = useState(true); // Initially, the overlay is shown
  const [isGuestAttended, setIsGuestAttended] = useState(false); // Track guest attendance

  // State for permission modal (iOS/Mac only)
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const hasInitializedRoomRef = useRef(false);

  // Test mode detection - check if this is a test meeting
  const userName = localStorage.getItem("username");
  const roomID = localStorage.getItem("demoId");
  const isTestMeeting = userName?.includes('TestUser') || roomID?.startsWith('test-');

  // Set startTime and scheduleTime based on demoInfo
  const startTime = new Date(demoInfo?.started_at);
  const scheduleTime = new Date(
    `${demoInfo?.schedule?.scheduled_date}T${demoInfo?.schedule?.scheduled_time}Z`
  );
  let minStayTime;

  // Calculate the minStayTime
  if (scheduleTime <= startTime || demoInfo?.soonest_at) {
    minStayTime = new Date(startTime.getTime() + 10 * 60 * 1000);
  } else {
    minStayTime = new Date(scheduleTime.getTime() + 10 * 60 * 1000);
  }

  const nowTime = new Date();
  const [countdown, setCountdown] = useState(calculateCountdown(minStayTime));

  const isHost = loggedIn;

  /**
   * Updates local demo info after the host toggles multiple-guests allowance.
   *
   * @param {Object} [updatedData] - Response data from the toggle API.
   * @param {number} [updatedData.max_guests_allowed] - Updated guest limit.
   * @param {boolean} [updatedData.is_multiple_guests_allowed] - Updated toggle flag.
   * @returns {void}
   */
  const handleMultipleGuestsUpdated = useCallback((updatedData) => {
    if (!updatedData) {
      return;
    }
    setDemoInfo((prev) =>
      prev
        ? {
            ...prev,
            max_guests_allowed: updatedData.max_guests_allowed ?? prev.max_guests_allowed,
            is_multiple_guests_allowed:
              updatedData.is_multiple_guests_allowed ?? prev.is_multiple_guests_allowed,
          }
        : prev
    );
  }, []);

  const status = useNetworkPing({
    url: NETWORK_PING_URL,
    interval: NETWORK_PING_INTERVAL,
    timeout: NETWORK_PING_TIMEOUT
  });

  /**
   * Detects if the user is on iOS or Mac
   * @returns {boolean} True if user is on iOS or Mac, false otherwise
   */
  const isIOSOrMac = () => {
    const ua = navigator.userAgent;
    return ua.includes('iPhone') || ua.includes('iPad') || ua.includes('Mac');
  };

  useEffect(() => {
    // Display the alert only once when minStayTime is in the future
    if (minStayTime > nowTime && !hasShownAlert) {
      setShowComissionAlert(true);
      setHasShownAlert(true);
    }

    // Update the countdown and hide the alert when time has passed
    if (minStayTime > nowTime) {
      const intervalId = setInterval(() => {
        setCountdown(calculateCountdown(minStayTime));
      }, 1000);

      return () => clearInterval(intervalId);
    } else {
      setShowComissionAlert(false); // Hide the alert if minStayTime has passed
    }
  }, [minStayTime, hasShownAlert, nowTime]);

  // Host presence heartbeat: while a HOST runs a live (fishing) demo, ping the
  // backend every 30s so a silent disconnect (closed tab/crash/network) drops
  // the demo off the badge instead of leaving a host-less room advertised.
  useEffect(() => {
    const liveDemoId = demoInfo?.id || localStorage.getItem("demoId");
    if (!(isHost && demoInfo?.is_live_meeting && liveDemoId)) return;
    const ping = () => hostHeartbeat(liveDemoId).catch(() => {});
    ping(); // fire immediately on join
    const intervalId = setInterval(ping, 30000);
    return () => clearInterval(intervalId);
  }, [isHost, demoInfo?.is_live_meeting, demoInfo?.id]);

  /**
   * Calculates the countdown time (minutes and seconds) from the current time to the target time.
   *
   * @param {Date} targetTime - The target time to calculate the countdown for.
   * @returns {{minutes: number, seconds: number}} - An object containing the remaining minutes and seconds.
   *   - `minutes`: Number of full minutes remaining.
   *   - `seconds`: Number of remaining seconds (0-59).
   */
  function calculateCountdown(targetTime) {
    const now = new Date();
    const difference = targetTime.getTime() - now.getTime();
    const seconds = Math.floor(difference / 1000);

    return {
      minutes: Math.floor(seconds / 60),
      seconds: seconds % 60,
    };
  }

  // Rest of your code remains unchanged
  const [stateDataForVideoMeeting, setStateDataForVideoMeeting] = useState(
    JSON.parse(localStorage.getItem("DETAILS"))
  );
  const demo_id = localStorage.getItem("demoId");

  /**
   * Closes the recording alert modal and opens the multiple-guests modal for hosts.
   *
   * @returns {void}
   */
  const handleCloseRecordingModal = useCallback(() => {
    setShow(false);
    // Only prompt about extra guests when the store's plan includes group
    // meetings, otherwise the host is offered a choice they cannot make.
    if (
      loggedIn &&
      (demoInfo?.id || demo_id) &&
      demoInfo?.group_meetings_enabled
    ) {
      setShowMultipleGuestsModal(true);
    }
  }, [loggedIn, demoInfo?.id, demo_id, demoInfo?.group_meetings_enabled]);

  /**
   * Dismisses the multiple-guests modal without changing the setting via buttons.
   *
   * @returns {void}
   */
  const handleCloseMultipleGuestsModal = useCallback(() => {
    setShowMultipleGuestsModal(false);
  }, []);

  const { createCall, room, participants, connected, destroyRoom, markModerationLeave, questionBanner } = useRoom({
    setIsLoading,
  });

  const { sendMessage } = useContext(SocketContext);

  const join_room = {
    command: "join_room",
    room_id: demo_id,
  };

  // Detect page refresh and redirect to waiting room
  // Check if user came from waiting room or refreshed the page
  useLayoutEffect(() => {
    // Skip refresh redirect for test meetings
    if (isTestMeeting) {
      return;
    }

    const demoIdFromStorage = localStorage.getItem('demoId');
    const cameFromWaitingRoom = sessionStorage.getItem('cameFromWaitingRoom');

    // If user didn't come from waiting room (page refresh) and demo_id exists, redirect
    if (!cameFromWaitingRoom && demoIdFromStorage) {
      // Use window.location.replace to force a proper navigation
      window.location.replace(`/demo/waiting-room/${demoIdFromStorage}`);
      return;
    }

    // Clear the flag after checking so subsequent refreshes will redirect
    if (cameFromWaitingRoom) {
      sessionStorage.removeItem('cameFromWaitingRoom');
    }

    // Cleanup: Remove the flag on component unmount
    return () => {
      sessionStorage.removeItem('cameFromWaitingRoom');
    };
  }, [isTestMeeting]);

  useEffect(() => {
    // Skip WebSocket join for test meetings
    if (!isTestMeeting) {
      joinRoomMessage();
    }
  }, [isTestMeeting]);

  /**
   * Sends a join room message to the WebSocket server.
   * This function is used to notify the server that the user wants to join the video meeting room.
   *
   * @async
   * @function joinRoomMessage
   * @returns {Promise<void>} A promise that resolves when the message has been sent.
   * @throws {Error} If the WebSocket connection fails or the message cannot be sent.
   */
  const joinRoomMessage = async () => {
    await sendMessage(join_room);
  };

  // Cleanup function to destroy refs on component unmount (only for hosts)
  useEffect(() => {
    return () => {
      hasInitializedRoomRef.current = false;
      // Only perform cleanup if user is a host (logged in)
      if (user && loggedIn) {
        // Cleanup room container ref
        if (roomContainer?.current) {
          roomContainer.current = null;
        }

        // Cleanup publisher container ref
        if (publisherContainer?.current) {
          publisherContainer.current = null;
        }

        // Cleanup room instance using the destroyRoom function from useRoom hook
        destroyRoom();
      }
    };
  }, [destroyRoom, user, loggedIn]);

  const roomContainer = useRef();
  const publisherContainer = useRef();
  const token = localStorage.getItem("ZEGO_TOKEN");

  /**
   * Initializes the Zego video meeting room.
   * This function handles the setup of the video call, including cleaning up existing streams,
   * validating audio/video states, and creating the Zego room connection.
   *
   * @function initializeRoom
   * @callback
   * @returns {void}
   *
   * @description
   * The function performs the following operations:
   * 1. Cleans up any existing media streams (video and audio elements)
   * 2. Validates that token, audioStatus, and videoStatus are available
   * 3. Creates a new Zego room call with the provided configuration
   * 4. Handles errors by displaying user-friendly error messages
   *
   * @throws {Error} Displays error message if room initialization fails
   */
  const initializeRoom = useCallback(() => {
    if (hasInitializedRoomRef.current) {
      return;
    }
    hasInitializedRoomRef.current = true;
    /**
     * Cleans up all existing media streams from the DOM.
     * This includes stopping all video and audio tracks, removing srcObject references,
     * and releasing any held device permissions.
     *
     * @function cleanupExistingStreams
     * @inner
     * @returns {void}
     *
     * @description
     * The function:
     * 1. Finds all video elements and stops their tracks
     * 2. Finds all audio elements and stops their tracks
     * 3. Releases media device permissions by requesting empty streams
     * 4. Handles errors silently during cleanup
     */
    const cleanupExistingStreams = () => {
      try {
        // Clean up all video elements
        const videos = document.querySelectorAll('video');
        videos.forEach(video => {
          if (video.srcObject) {
            const stream = video.srcObject;
            if (stream && stream.getTracks) {
              // Stop all audio and video tracks
              stream.getTracks().forEach(track => {
                track.stop();
              });
            }
            video.srcObject = null;
          }
        });

        // Clean up all audio elements
        const audios = document.querySelectorAll('audio');
        audios.forEach(audio => {
          if (audio.srcObject) {
            const stream = audio.srcObject;
            if (stream && stream.getTracks) {
              stream.getTracks().forEach(track => {
                track.stop();
              });
            }
            audio.srcObject = null;
          }
        });

        // Clean up any remaining media streams in the global scope
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          // This helps release any held device permissions
          navigator.mediaDevices.getUserMedia({ video: false, audio: false })
            .then(stream => {
              stream.getTracks().forEach(track => track.stop());
            })
            .catch(() => {
              // Ignore errors as this is just for cleanup
            });
        }
      } catch (er) {
        logger.error('Error cleaning up existing streams:', er);
      }
    };

    cleanupExistingStreams();

    // try fetching the audio video state from previous component
    if (
      token &&
      audioStatus !== null &&
      audioStatus !== undefined &&
      videoStatus !== null &&
      videoStatus !== undefined
    ) {

      const zegoMeetingDetails =
        stateDataForVideoMeeting?.sessionDetail?.meeting_details || {};

      const zegoUserId = loggedIn
        ? zegoMeetingDetails?.host_zego_user_id || userName
        : zegoMeetingDetails?.guest_zego_user_id || userName;

      const zegoUserName = loggedIn
        ? zegoMeetingDetails?.host_zego_user_name || userName
        : zegoMeetingDetails?.guest_zego_user_name || userName;

      createCall(
        {
          appID: process.env.REACT_APP_ZEGO_CLOUD_APP_ID,
          serverSecret: process.env.REACT_APP_ZEGO_CLOUD_SERVER_SECRET,
          roomID: roomID,
          userID: String(zegoUserId),
          userName: zegoUserName,
          setShowOverlay: setShowOverlay,
        },
        roomContainer.current,
        audioStatus,
        videoStatus
      ).catch((err) => {
        hasInitializedRoomRef.current = false;
        logger.error("ZEGO createCall failed:", err);
        messageApi.open({
          type: 'error',
          content: 'Something went wrong while connecting you to the call. If you are not able coordinate with the other person, please try to refresh the page.',
          duration: 10
        });
      });

    } else if (
      audioStatus === null ||
      audioStatus === undefined ||
      videoStatus === null ||
      videoStatus === undefined
    ) {
      setError({ message: 'Some Technical Error, try again' });
      logger.warn('Could not get the audio and video state');
    } else {
      setError({ message: 'credentials to the meeting room not found' });
      logger.warn('User is not allowed here - credentials not found');
    }
  }, [token, audioStatus, videoStatus, createCall, roomID, userName, setShowOverlay, messageApi]);

  useEffectOnce(() => {
    // Prevent auto-init unless user actually came from Join Call click
    if (!state?.allowRoomStart) {
      logger.warn('Blocked room initialization: no explicit user gesture found');
      return;
    }

    // Check if user is on iOS or Mac
    if (isIOSOrMac()) {
      // Show permission modal for iOS/Mac users
      setShowPermissionModal(true);
    } else {
      initializeRoom();
    }
  });


  /**
   * Handles the proceed button click in the permission modal.
   * This function is called when the user clicks "Proceed" on the iOS/Mac permission modal.
   * It closes the permission modal and allows the room initialization to proceed.
   *
   * @function handleProceed
   * @returns {void}
   *
   * @description
   * When called, this function:
   * 1. Hides the permission modal by setting showPermissionModal to false
   * 2. Enables room initialization by setting canProceedToRoom to true
   * 3. This triggers the useEffect that calls initializeRoom()
   */
  const handleProceed = () => {
    setShowPermissionModal(false);
    initializeRoom();
  };


  /**
   * Renders a static overlay with commission information and countdown timer.
   * This overlay is displayed to hosts while waiting for guests to join the meeting.
   *
   * @function renderOverlay
   * @param {Object} info - The demo information object containing commission details.
   * @param {Object} [info.commission] - Commission configuration object.
   * @param {number|string} [info.commission.no_show_demo_commission] - No-show commission amount.
   * @param {string} [info.commission.no_show_demo_commission_type] - Type of no-show commission ('AMOUNT' or 'PERCENTAGE').
   * @param {number|string} [info.commission.demo_commission] - Demo commission amount.
   * @param {string} [info.commission.demo_commission_type] - Type of demo commission ('AMOUNT' or 'PERCENTAGE').
   *
   * @returns {JSX.Element} A React element containing the overlay with commission information.
   *
   * @description
   * The overlay displays different messages based on the countdown timer:
   * - If countdown is invalid: "Waiting for the guest...."
   * - If countdown > 0: Shows remaining time until qualifying for no-show commission
   * - If countdown <= 0: Shows success message with both no-show and demo commission amounts
   */
  const renderOverlay = (info) => {
    const commission = info?.commission;
    const {
      no_show_demo_commission: noShowAmount,
      demo_commission: demoAmount,
      no_show_demo_commission_type: noShowType,
      demo_commission_type: demoType,
    } = commission || {};

    const GuestNoShowCommission =
      noShowType === "AMOUNT" ? `$${noShowAmount}` : `${noShowAmount}%`;
    const GuestDemoCommission =
      demoType === "AMOUNT" ? `$${demoAmount}` : `${demoAmount}%`;

    /**
     * Gets the overlay message text and style based on the current countdown state.
     *
     * @function getOverlayMessage
     * @inner
     * @returns {Object} An object containing the message text and style.
     * @returns {string} returns.text - The message text to display.
     * @returns {Object} returns.style - The CSS style object for the text.
     *
     * @description
     * Returns different messages based on countdown state:
     * - Invalid countdown: "Waiting for the guest...." with default white text
     * - Countdown > 0: Shows time remaining with default white text
     * - Countdown <= 0: Shows success message with green text
     */
    const getOverlayMessage = () => {
      const minutes = parseInt(countdown?.minutes, 10);
      const seconds = parseInt(countdown?.seconds, 10);

      if (Number.isNaN(minutes) || Number.isNaN(seconds)) {
        return {
          text: "Waiting for the guest....",
          style: overlayTextStyle,
        };
      }

      if (minutes >= 1 || seconds >= 1) {
        return {
          text: `${minutes}:${seconds} until you qualify for ${GuestNoShowCommission}`,
          style: overlayTextStyle,
        };
      }

      return {
        text: `You have qualified for ${GuestNoShowCommission}, please stay on the line in case the guest shows up and you can earn ${GuestDemoCommission}.`,
        style: overlayStyleForSuccess,
      };
    };

    const { text, style } = getOverlayMessage();

    return (
      <div style={overlayStyle}>
        <p style={style}>{text}</p>
      </div>
    );
  };

  // Styles for the overlay and text
  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "97%",
    backgroundColor: "transparent",
    zIndex: 199, // Ensure it appears over the ZegoCloud container
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none"
  };

  const overlayTextStyle = {
    fontSize: "24px",
    color: "white",
  };

  const overlayStyleForSuccess = {
    fontSize: "24px",
    color: "green",
  };
  useEffect(() => {
    // Test mode: Create mock demo data and skip API calls
    if (isTestMeeting) {
      const mockDemoInfo = {
        id: roomID,
        status: 'live',
        started_at: new Date().toISOString(),
        schedule: {
          scheduled_date: new Date().toISOString().split('T')[0],
          scheduled_time: new Date().toTimeString().split(' ')[0]
        },
        members: {
          host_attend_meeting: new Date().toISOString(),
          guest_detail: {
            attend_meeting: new Date().toISOString()
          }
        },
        commission: {
          no_show_demo_commission: 10,
          demo_commission: 25,
          no_show_demo_commission_type: 'AMOUNT',
          demo_commission_type: 'AMOUNT'
        }
      };

      setDemoInfo(mockDemoInfo);
      setHostAttendMeeting(mockDemoInfo.members.host_attend_meeting);
      setIsGuestAttended(true);
      setShowOverlay(false); // Don't show overlay for test meetings
      return;
    }

    // Normal mode: Fetch real demo data from API
    const res = AuthenticatedDemoStatus(demo_id);
    res.then((data) => {
      setDemoInfo(data?.data?.data);
      setHostAttendMeeting(data?.data?.data?.members?.host_attend_meeting);
      const primaryJoined =
        data?.data?.data?.members?.guest_detail?.attend_meeting;

      const extraJoined =
        data?.data?.data?.members?.extra_guests?.some(
          g => g?.guest_detail?.attend_meeting
        ) || false;

      const anyGuestJoined = primaryJoined || extraJoined;

      setIsGuestAttended(!!anyGuestJoined);
    });
  }, [participants, isTestMeeting]);

  useEffect(() => {
    setShowOverlay(participants.length < 1);
  }, [participants]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (remainingTime > 0) {
        setRemainingTime(remainingTime - 1);
      } else {
        if (hostAttendMeeting === null) {
          if (!loggedIn) {
            setShowRescheduleModal(true);
          }
        }
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [remainingTime]);

  useEffect(() => {
    if (
      document
        .getElementById("layoutContainer")
        ?.getElementsByClassName(
          "OT_root OT_subscriber OT_fit-mode-cover ot-layout"
        )[0] &&
      document
        .getElementById("layoutContainer")
        .getElementsByClassName(
          "OT_root OT_subscriber OT_fit-mode-cover ot-layout"
        )[0].clientHeight < 350
    ) {
      document
        .getElementById("layoutContainer")
        .getElementsByClassName(
          "OT_root OT_subscriber OT_fit-mode-cover ot-layout"
        )[0].style.top = "50%";
    } else {
    }
  }, [stateDataForVideoMeeting]);

  useEffect(() => {
    if (stateDataForVideoMeeting?.videoState === false) {
      setVideoStatus(false);
      room?.camera?.disableVideo();
    } else {
      setVideoStatus(true);
      room?.camera?.enableVideo();
    }
    if (stateDataForVideoMeeting?.audioState === false) {
      setAudioStatus(false);
      room?.camera?.disableAudio();
    } else {
      setAudioStatus(true);
      room?.camera?.enableAudio();
    }
  }, [stateDataForVideoMeeting]);

  useEffect(() => {
    if (participants.length === 0) {
      setShowA(true);
    } else {
      setShowA(false);
    }
  }, [participants]);

  useEffect(() => {
    // Skip recording modal for test meetings
    if (isTestMeeting) {
      return;
    }

    if (connected && stateDataForVideoMeeting) {
      (async () => {
        if (
          stateDataForVideoMeeting?.sessionDetail?.meeting_details?.archive_id
        ) {
          handleShow();
        } else {
          try {
            let data = {
              record: true,
            };
            const res = await record(
              demo_id || stateDataForVideoMeeting?.sessionDetail?.id,
              data
            );
            handleShow();
          } catch (error) {
            logger.error('Issue Occured While Video meeting Recording', error);
          }
        }
      })();
    }
  }, [connected, stateDataForVideoMeeting, isTestMeeting]);

  useEffect(() => {
    if (!connected || isTestMeeting) {
      return undefined;
    }
    const demoIdentifier = demo_id || stateDataForVideoMeeting?.sessionDetail?.id;
    if (!demoIdentifier) {
      return undefined;
    }

    let cancelled = false;
    const leaveForModeration = (moderation) => {
      if (cancelled) {
        return;
      }
      markModerationLeave();
      try {
        room?.destroy?.();
      } catch (err) {
        logger.error("Failed to destroy room after moderation action", err);
      }
      const guestUserId =
        localStorage.getItem("extra_guest_id") ||
        demoInfo?.members?.guest_detail?.id ||
        stateDataForVideoMeeting?.sessionDetail?.members?.guest_detail?.id;
      if (loggedIn) {
        navigate("/user/welcome");
      } else {
        navigate(
          `/meeting-status-check?demo=${demoIdentifier}&uid=${guestUserId || ""}`
        );
      }
      if (moderation?.message) {
        messageApi.open({
          type: "error",
          content: moderation.message,
          duration: 8,
        });
      }
    };

    const checkModeration = async () => {
      try {
        const guestUserId =
          localStorage.getItem("extra_guest_id") ||
          demoInfo?.members?.guest_detail?.id;
        const response = loggedIn
          ? await AuthenticatedDemoStatus(demoIdentifier)
          : await publicDemoStatus(
              demoIdentifier,
              guestUserId,
              guestUserId ? "uid" : "none"
            );
        const payload = response?.data?.data;
        const moderation = payload?.moderation;
        if (moderation?.demo_terminated || moderation?.guest_removed) {
          leaveForModeration(moderation);
        }
      } catch (err) {
        logger.error("Moderation status poll failed", err);
      }
    };

    checkModeration();
    const intervalId = setInterval(checkModeration, MODERATION_STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    connected,
    isTestMeeting,
    loggedIn,
    demo_id,
    stateDataForVideoMeeting,
    demoInfo,
    AuthenticatedDemoStatus,
    publicDemoStatus,
    markModerationLeave,
    room,
    navigate,
    messageApi,
  ]);

  if (error) {
    return (
      <div className="card cardc text-center h4 p-5">
        There was an error fetching the data from the server
      </div>
    );
  }

  /**
   * Gets the CSS style object for the participant container based on the number of participants.
   * This function adjusts the container size to provide optimal video display layout.
   *
   * @function getParticipantContainerStyle
   * @param {number} participantsCount - The total number of participants in the meeting.
   *
   * @returns {Object} A CSS style object with width and height properties.
   * @returns {string} returns.width - The width of the container (either "30%" or "150px").
   * @returns {string} returns.height - The height of the container (either "120px" or "150px").
   *
   * @description
   * The function returns different styles based on participant count:
   * - 1 participant: Larger container (30% width, 120px height) for single user view
   * - 2+ participants: Smaller container (150px x 150px) for multi-user grid view
   */
  const getParticipantContainerStyle = (participantsCount) => {
    if (participantsCount === 1) {
      return { width: "30%", height: "120px" };
    } else {
      return { width: "150px", height: "150px" };
    }
  };
  const publisherContainerStyle = getParticipantContainerStyle(
    participants.length
  );
  return (
    <div id="callContainer" className="room-container-custom">
      {contextHolder}
      {!isLoading ? (
        <div className="position-relative" style={{ height: "95%" }}>
          <div
            id="roomContainer"
            style={{ height: "100vh", maxWidth: "100vw" }}
            ref={roomContainer}
          ></div>
          {showOverlay && loggedIn && renderOverlay(demoInfo)}
          {loggedIn && (
            <QuestionBannerOverlay
              banners={questionBanner.banners}
              onDismiss={questionBanner.dismiss}
              participants={participants}
            />
          )}
          <div
            id="publisherContainer"
            style={publisherContainerStyle}
            className="position-absolute"
            ref={publisherContainer}
          >
            <RecordingModal
              show={show}
              handleClose={handleCloseRecordingModal}
              isHost={isHost}
            />
            {isHost &&
              (demoInfo?.id || demo_id) &&
              demoInfo?.group_meetings_enabled && (
              <AllowMultipleGuestsModal
                show={showMultipleGuestsModal}
                handleClose={handleCloseMultipleGuestsModal}
                demoId={demoInfo?.id || demo_id}
                maxGuestsAllowed={demoInfo?.max_guests_allowed}
                isMultipleGuestsAllowed={demoInfo?.is_multiple_guests_allowed}
                onMultipleGuestsUpdated={handleMultipleGuestsUpdated}
              />
            )}
            <NetworkPopup status={status} />


            {/* TODO: This old fallback logic is no longer in use as per new workflow but can be uncommented if required in future */}

            {/* {showReScheduleModal && (
              <DemoInCallModal
                showReScheduleModal={showReScheduleModal}
                setShowRescheduleModal={setShowRescheduleModal}
                demo_id={demo_id}
                demoInfo={demoInfo}
                setRemainingTime={setRemainingTime}
                remainingTime={remainingTime}
              />
            )} */}
          </div>
        </div>
      ) : (
        <PurebrandLoader />
      )}
      {/* Permission Modal for iOS/Mac users */}
      <Modal
        show={showPermissionModal}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>{VIDEO_MEETING_PERMISSION_MODAL.TITLE}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {VIDEO_MEETING_PERMISSION_MODAL.MESSAGE}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleProceed}>
            {VIDEO_MEETING_PERMISSION_MODAL.BUTTON_TEXT}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
